import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import '../styles/components/BlogAudioPlayer.css';

const BlogAudioPlayer = ({ postId, postTitle, content }) => {
  const [loading, setLoading] = useState(true);
  const [audioData, setAudioData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  
  const audioRef = useRef(null);

  useEffect(() => {
    // Only check if audio already exists, but don't generate yet
    checkAudioExists();
  }, [postId]);

  const checkAudioExists = async () => {
    try {
      setLoading(true);
      const response = await api.getAudio(postId);
      if (response.success && response.data?.audio_url) {
        setAudioData(response.data);
      }
    } catch (error) {
      console.log('No existing audio, will generate on first play');
    } finally {
      setLoading(false);
    }
  };

  const generateAndPlay = async () => {
    setLoading(true);
    toast.info('🎧 Generating audio with ElevenLabs...', { autoClose: 2000 });
    try {
      const response = await api.generateAudio(postId);
      if (response.success && response.data?.audio_url) {
        setAudioData(response.data);
        toast.success('✅ Audio ready!');
        return true;
      } else {
        throw new Error(response.message || 'Generation failed');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('❌ Failed to generate audio. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = async () => {
    // If no audio data yet, generate first
    if (!audioData) {
      const success = await generateAndPlay();
      if (!success) return;
      // Small delay to ensure audio element is rendered
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(e => toast.error('Playback failed'));
          setIsPlaying(true);
        }
      }, 200);
      return;
    }

    // Audio exists – toggle play/pause
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    const seekTime = (e.target.value / 100) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackRate(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  // Show minimal loading state while checking for existing audio
  if (loading && !audioData) {
    return (
      <div className="blog-audio-player player-active">
        <div className="audio-player-header">
          <div className="audio-icon"><i className="fa-solid fa-headphones"></i></div>
          <div className="audio-title"><h4>Loading audio...</h4></div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-audio-player player-active">
      {audioData && (
        <audio
          ref={audioRef}
          src={`https://supernovalab.ai${audioData.audio_url}`}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
        />
      )}

      <div className="audio-player-header">
        <div className="audio-icon">
          <i className="fa-solid fa-headphones"></i>
        </div>
        <div className="audio-title">
          <h4>Listen to Article (ElevenLabs)</h4>
        </div>
      </div>

      <div className="audio-progress-section">
        <button className="btn-skip" onClick={() => skip(-10)} title="Rewind 10s">
          <i className="fa-solid fa-backward-step"></i>
        </button>
        <button className="btn-play-main" onClick={togglePlay}>
          {isPlaying ? <i className="fa-solid fa-pause"></i> : <i className="fa-solid fa-play"></i>}
        </button>
        <button className="btn-skip" onClick={() => skip(10)} title="Forward 10s">
          <i className="fa-solid fa-forward-step"></i>
        </button>
        <span className="time-current">{formatTime(currentTime)}</span>
        <div className="progress-bar-container">
          <input
            type="range"
            min="0"
            max="100"
            value={progressPercentage}
            onChange={handleSeek}
            className="progress-slider"
          />
          <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
        </div>
        <span className="time-duration">{formatTime(duration)}</span>
      </div>

      <div className="audio-additional-controls">
        <div className="speed-control">
          {[0.75, 1, 1.25, 1.5, 2].map(speed => (
            <button
              key={speed}
              className={`btn-speed ${playbackRate === speed ? 'active' : ''}`}
              onClick={() => handleSpeedChange(speed)}
            >
              {speed}x
            </button>
          ))}
        </div>
        <div className="volume-control">
          <i className="fa-solid fa-volume-high"></i>
          <input
            type="range"
            min="0"
            max="100"
            value={volume * 100}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
        </div>
      </div>
    </div>
  );
};

export default BlogAudioPlayer;